import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, CalendarClock, Video, Building2, CheckCircle2, MapPin } from "lucide-react";

export default function BookingWidget({ token, basePath, cliente, appointment: initialAppt }) {
  const base = basePath || `/public/consulenza/${token}`;
  const [days, setDays] = useState(null);
  const [facility, setFacility] = useState("");
  const [mode, setMode] = useState("online");
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState(cliente?.name || "");
  const [email, setEmail] = useState(cliente?.email || "");
  const [phone, setPhone] = useState(cliente?.phone || "");
  const [booking, setBooking] = useState(false);
  const [appt, setAppt] = useState(initialAppt || null);
  const [hasInstallers, setHasInstallers] = useState(false);

  useEffect(() => {
    api.get(`${base}/slots`)
      .then((r) => { setDays(r.data.days || []); setFacility(r.data.facility || ""); })
      .catch(() => setDays([]));
  }, [token]);

  useEffect(() => {
    api.get("/public/installers-map")
      .then((r) => setHasInstallers((r.data.items || []).length > 0))
      .catch(() => setHasInstallers(false));
  }, []);

  const InstallerLink = () => (
    hasInstallers ? (
      <div className="mt-4 pt-4 border-t border-border text-center" data-testid="installer-partner-cta">
        <a href="/mappa-installatori" target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          data-testid="installer-partner-link">
          <MapPin className="h-4 w-4" /> Oppure contatta un nostro installatore partner
        </a>
      </div>
    ) : null
  );

  const book = async () => {
    if (!selected) { toast.error("Seleziona un orario"); return; }
    if (!email || !email.includes("@")) { toast.error("Inserisci una email valida"); return; }
    setBooking(true);
    try {
      const { data } = await api.post(`${base}/book`, { start: selected, mode, name, email, phone });
      setAppt(data.appointment);
      toast.success("Appuntamento confermato!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Prenotazione non riuscita");
    } finally { setBooking(false); }
  };

  if (appt) {
    const d = new Date(appt.start);
    return (
      <div className="rounded-xl border border-green-500/40 bg-green-500/10 p-6" data-testid="booking-confirmed">
        <div className="flex items-center gap-2 text-green-600 font-heading font-bold text-lg mb-2"><CheckCircle2 className="h-5 w-5" /> Appuntamento confermato</div>
        <p className="text-sm">{d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", timeZone: "Europe/Rome" })} alle <b>{d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}</b> — {appt.mode === "online" ? "Online" : "In visita allo stabilimento"}</p>
        {appt.mode === "online" && (appt.meet_link ? <a href={appt.meet_link} className="text-sm text-primary underline" target="_blank" rel="noreferrer">Link riunione</a> : <p className="text-xs text-muted-foreground mt-1">Il link della riunione ti verrà inviato prima dell'appuntamento.</p>)}
        {appt.mode === "visita" && <p className="text-xs text-muted-foreground mt-1">{appt.location || facility}</p>}
        <InstallerLink />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-6" data-testid="booking-widget">
      <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Prenota la tua consulenza</h3>
      <p className="text-sm text-muted-foreground mb-4">Fissa un incontro con un nostro consulente: durante l'appuntamento ti illustreremo l'analisi e il preventivo dedicato.</p>

      <div className="flex gap-2 mb-4">
        {[["online", "Online", Video], ["visita", "In visita", Building2]].map(([v, l, Icon]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`booking-mode-${v}`}
            className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${mode === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {mode === "visita" && facility && <p className="text-xs text-muted-foreground mb-3">Sede: {facility}</p>}

      {days === null ? (
        <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : days.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun orario disponibile al momento. Contattaci direttamente.</p>
      ) : (
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto pr-1">
          {days.map((d) => (
            <div key={d.date}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">{d.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {d.slots.map((s) => (
                  <button key={s.start} onClick={() => setSelected(s.start)} data-testid={`slot-${s.start}`}
                    className={`text-xs rounded-md border px-2.5 py-1.5 transition-colors ${selected === s.start ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {days && days.length > 0 && (
        <>
          <div className="grid sm:grid-cols-3 gap-2 mb-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" data-testid="booking-name" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="booking-email" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono" data-testid="booking-phone" />
          </div>
          <Button onClick={book} disabled={booking || !selected} className="w-full font-semibold" data-testid="booking-submit">
            {booking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1.5" />} Conferma appuntamento
          </Button>
        </>
      )}
      <InstallerLink />
    </div>
  );
}
